import React from 'react';
import styled, { useTheme } from 'styled-components';
import { motion } from 'framer-motion';

const TypingWrapper = styled(motion.div)`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-top: 12px;
  animation: slideInUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const Avatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  box-shadow: 0 4px 12px ${props => props.theme.colors.primary}15;
  border: 2px solid ${props => props.theme.colors.background};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  /* Subtle pulse animation */
  animation: pulse 2s ease-in-out infinite;
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
`;

const TypingContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 16px 20px;
  background: ${props => `linear-gradient(135deg, ${props.theme.colors.botMessageBackground} 0%, ${props.theme.colors.botMessageBackground}f5 100%)`};
  border-radius: 20px 20px 20px 6px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.1);
  position: relative;
  
  /* Simple rounded corner design without pseudo-elements */
  border-radius: 20px 20px 20px 4px;
  
  /* Breathing animation */
  animation: breathe 1.5s ease-in-out infinite;
  
  @keyframes breathe {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.02); }
  }
`;

const Dot = styled(motion.div)`
  width: 8px;
  height: 8px;
  background: ${props => `linear-gradient(135deg, ${props.theme.colors.primary} 0%, ${props.theme.colors.primary}dd 100%)`};
  border-radius: 50%;
  box-shadow: 0 2px 4px ${props => props.theme.colors.primary}20;
`;

const dotVariants = {
  animate: {
    y: [0, -8, 0],
    scale: [1, 1.2, 1],
    transition: {
      duration: 0.8,
      repeat: Infinity,
      repeatType: "loop",
      ease: "easeInOut"
    }
  }
};

const containerVariants = {
  initial: { opacity: 0, y: 20, scale: 0.9 },
  animate: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 25
    }
  },
  exit: { 
    opacity: 0, 
    y: -10, 
    scale: 0.95,
    transition: {
      duration: 0.2
    }
  }
};

const TypingIndicator = ({ botAvatar }) => {
  const theme = useTheme();
  
  return (
    <TypingWrapper
      theme={theme}
      variants={containerVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <Avatar theme={theme} src={botAvatar} alt="Bot Avatar" />
      <TypingContainer theme={theme}>
        {[0, 1, 2].map((index) => (
          <Dot
            key={index}
            theme={theme}
            variants={dotVariants}
            animate="animate"
            transition={{
              delay: index * 0.2
            }}
          />
        ))}
      </TypingContainer>
    </TypingWrapper>
  );
};

export default TypingIndicator;